import axios from 'axios';
import TokenManager from '../utils/tokenManager';

// Create axios instance with enhanced configuration
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000,
    withCredentials: false,
});

api.interceptors.request.use(
    (config) => {
        const timestamp = new Date().toISOString();
        console.log(`[API ${timestamp}] ${config.method?.toUpperCase()} ${config.url}`);

        const token = TokenManager.getToken();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log(`[API ${timestamp}] Authorization header set`);
            
            if (TokenManager.needsRefresh()) {
                console.log(`[API ${timestamp}] Token needs refresh soon`);
                window.dispatchEvent(new CustomEvent('auth:token:refresh:suggested', {
                    detail: { timestamp }
                }));
            }
        } else {
            console.log(`[API ${timestamp}] No valid token available`);
        }

        return config;
    },
    (error) => {
        console.error('[API] Request interceptor error:', error);
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        const timestamp = new Date().toISOString();
        console.log(`[API ${timestamp}] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        const timestamp = new Date().toISOString();

        if (error.response) {
            console.error(`[API ${timestamp}] ${error.response.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
            
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                console.log(`[API ${timestamp}] 401 Unauthorized - attempting token refresh`);

                try {
                    const refreshEvent = new CustomEvent('auth:token:refresh:required', {
                        detail: { 
                            timestamp,
                            originalUrl: originalRequest.url,
                            method: originalRequest.method 
                        }
                    });
                    window.dispatchEvent(refreshEvent);

                    const refreshResult = await new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => {
                            cleanup();
                            reject(new Error('Token refresh timeout after 15 seconds'));
                        }, 15000);

                        const handleRefreshComplete = (event) => {
                            cleanup();
                            if (event.detail.success) {
                                resolve(event.detail.token);
                            } else {
                                reject(new Error(event.detail.error || 'Token refresh failed'));
                            }
                        };

                        const cleanup = () => {
                            clearTimeout(timeout);
                            window.removeEventListener('auth:token:refresh:complete', handleRefreshComplete);
                        };

                        window.addEventListener('auth:token:refresh:complete', handleRefreshComplete);
                    });

                    console.log(`[API ${timestamp}] Token refreshed successfully, retrying request`);

                    // Update authorization header and retry original request
                    originalRequest.headers.Authorization = `Bearer ${refreshResult}`;
                    return api(originalRequest);

                } catch (refreshError) {
                    console.error(`[API ${timestamp}] Token refresh failed:`, refreshError);

                    TokenManager.clearToken();

                    window.dispatchEvent(new CustomEvent('auth:logout:required', {
                        detail: { 
                            reason: 'token_refresh_failed', 
                            error: refreshError.message,
                            timestamp 
                        }
                    }));

                    return Promise.reject(refreshError);
                }
            }

            // Handle other HTTP errors
            if (error.response.status >= 500) {
                console.error(`[API ${timestamp}] Server error:`, error.response.data);
            } else if (error.response.status >= 400) {
                console.error(`[API ${timestamp}] Client error:`, error.response.data);
            }

        } else if (error.request) {
            console.error(`[API ${timestamp}] Network error - no response received:`, error.request);
        } else {
            console.error(`[API ${timestamp}] Request setup error:`, error.message);
        }
        return Promise.reject(error);
    }
);

// Set default authorization header if valid token exists on initialization
const initToken = TokenManager.getToken();
if (initToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${initToken}`;
    console.log('[API] Default Authorization header set on initialization');
}

// Export the configured axios instance
export default api;
